import { zodResolver } from '@hookform/resolvers/zod';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import InputAdornment from '@mui/material/InputAdornment';
import type { InviteMemberDto } from 'api';
import { ChevronDown, Mail, X } from 'lucide-react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  FormFieldLabel,
  FormSelectMenuItem,
  formSelectMenuProps,
  FormTextField,
  InviteFormDialogActions,
  InviteFormDialogContent,
  InviteFormFieldStack,
  ModalCancelButton,
  ModalCloseButton,
  ModalDescription,
  ModalSubmitButton,
  ModalTitle,
} from '../team.styles';

const inviteSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email'),
  role: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

interface InviteMemberModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: InviteMemberDto) => void;
  isPending?: boolean;
  isViewerOwner?: boolean;
}

export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  open,
  onClose,
  onSubmit,
  isPending = false,
  isViewerOwner = false,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', role: 3 },
  });

  useEffect(() => {
    if (open) reset({ email: '', role: 3 });
  }, [open, reset]);

  const handleFormSubmit = (values: InviteFormValues) => {
    onSubmit({ email: values.email, role: values.role });
  };

  const roleOptions = [
    ...(isViewerOwner ? [{ value: 0, label: 'Owner' }] : []),
    { value: 1, label: 'Admin' },
    { value: 2, label: 'Project Manager' },
    { value: 3, label: 'Team Member' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: '18px', position: 'relative' } } }}
    >
      <ModalCloseButton onClick={onClose} disabled={isPending} aria-label="close">
        <X size={18} />
      </ModalCloseButton>
      <ModalTitle>Invite new member</ModalTitle>
      <ModalDescription>Send an invitation to join your team workspace.</ModalDescription>

      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <InviteFormDialogContent>
          <InviteFormFieldStack spacing={2}>
            <div>
              <FormFieldLabel>Email address</FormFieldLabel>
              <FormTextField
                type="email"
                required
                autoFocus
                fullWidth
                size="small"
                placeholder="colleague@company.com"
                error={Boolean(errors.email)}
                helperText={errors.email?.message}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail size={16} />
                      </InputAdornment>
                    ),
                  },
                }}
                {...register('email')}
              />
            </div>
            <div>
              <FormFieldLabel>Role</FormFieldLabel>
              <FormTextField
                select
                fullWidth
                size="small"
                defaultValue={3}
                error={Boolean(errors.role)}
                helperText={errors.role?.message}
                slotProps={{
                  select: {
                    IconComponent: (props) => <ChevronDown {...props} size={16} strokeWidth={2} />,
                    MenuProps: formSelectMenuProps,
                  },
                }}
                {...register('role', { valueAsNumber: true })}
              >
                {roleOptions.map((opt) => (
                  <FormSelectMenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </FormSelectMenuItem>
                ))}
              </FormTextField>
            </div>
          </InviteFormFieldStack>
        </InviteFormDialogContent>

        <InviteFormDialogActions>
          <ModalCancelButton onClick={onClose} disabled={isPending}>
            Cancel
          </ModalCancelButton>
          <ModalSubmitButton
            type="submit"
            variant="contained"
            color="secondary"
            disableElevation
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Send invite
          </ModalSubmitButton>
        </InviteFormDialogActions>
      </form>
    </Dialog>
  );
};
